import type { BuildingInStoreInfo, BuildingMenu, PoiContent } from '@/services/dto';
import type { FacilityCategoryItem, FacilityCategoryNearestPoi } from '@/types/facility-category';
import type { KioskSelectedCategory } from '@/services/dto/kiosk-config';
import poiUtils from '@/utils/poi';
import { findNearestFloorWithPoi } from '@/utils/map';
import type { PoiItem } from '@/types/map';

const poiMatchesCategoryCodes = (poi: PoiContent, categoryCodes: string[]): boolean => {
    const poiCategories: string[] = poi.categories ?? poi.rootCategories ?? [];
    return categoryCodes.some((code) => poiCategories.includes(code));
};

const filterPoisByCategory = (poiContents: PoiContent[], categoryCodes: string[]): PoiContent[] => {
    return poiContents.filter((poi) => poiMatchesCategoryCodes(poi, categoryCodes));
};

export const filterSelectedCategoriesByPoiContents = (
    categories: KioskSelectedCategory[] | undefined,
    poiContents: PoiContent[] | undefined
): KioskSelectedCategory[] => {
    if (!categories?.length) return [];
    return categories.filter((cat) => {
        const categoryCodes = cat.mergeCategoryCode ?? [cat.categoryCode];
        return (poiContents ?? []).some((poi) => poiMatchesCategoryCodes(poi, categoryCodes));
    });
};

export const mapSelectedCategoriesToFacilityItems = (
    categories: KioskSelectedCategory[] | undefined,
    poiContents: PoiContent[] | undefined
): FacilityCategoryItem[] => {
    if (!categories?.length) return [];

    return categories
        .map((cat) => {
            const categoryCodes = cat.mergeCategoryCode ?? [cat.categoryCode];
            const matchingPois = filterPoisByCategory(poiContents ?? [], categoryCodes);
            if (!matchingPois.length) return null;

            return {
                id: cat.id || cat.clientCode,
                name: cat.name,
                clientCode: cat.clientCode,
                categoryCodes,
            };
        })
        .filter((item): item is FacilityCategoryItem => item !== null);
};

export const resolveKioskCategoryFromFacilityItem = (
    item: FacilityCategoryItem,
    categories: KioskSelectedCategory[] | undefined
): KioskSelectedCategory | null => {
    if (!categories?.length) return null;
    return categories.find((cat) => cat.id === item.id || cat.clientCode === item.id) ?? null;
};

export const resolveNearestPoiForCategory = (
    category: FacilityCategoryItem,
    poiContents: PoiContent[] | undefined,
    buildings: BuildingInStoreInfo[] | undefined,
    currentFloorName?: string
): FacilityCategoryNearestPoi | null => {
    const matchingPois = filterPoisByCategory(poiContents ?? [], category.categoryCodes);
    if (!matchingPois.length) return null;

    if (currentFloorName && buildings?.length) {
        const building = buildings[0];
        const sortedFloors = [...(building.floors ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        const groupedPoiByFloorId = new Map<string, Map<string, PoiItem[]>>();

        matchingPois.forEach((poi) => {
            const floorName = poiUtils.getFloorName('ko', poi.studioFloorId, building.floors ?? []);
            if (!groupedPoiByFloorId.has(floorName)) {
                groupedPoiByFloorId.set(floorName, new Map());
            }
            category.categoryCodes.forEach((code) => {
                if (poiMatchesCategoryCodes(poi, [code])) {
                    const floorMap = groupedPoiByFloorId.get(floorName)!;
                    const existing = floorMap.get(code) ?? [];
                    floorMap.set(code, [
                        ...existing,
                        {
                            id: poi.studioPoiId,
                            floorId: poi.studioFloorId,
                            position: { x: 0, y: 0 },
                        } as PoiItem,
                    ]);
                }
            });
        });

        const nearestFloor = findNearestFloorWithPoi(sortedFloors, currentFloorName, groupedPoiByFloorId, category.categoryCodes);
        if (nearestFloor) {
            const floorName = nearestFloor.name['ko'] ?? '';
            const poiOnFloor = matchingPois.find((poi) => {
                const name = poiUtils.getFloorName('ko', poi.studioFloorId, building.floors ?? []);
                return name === floorName;
            });
            if (poiOnFloor) {
                return {
                    studioFloorId: poiOnFloor.studioFloorId,
                    studioPoiId: poiOnFloor.studioPoiId,
                    buildingId: poiOnFloor.studioBuildingId,
                };
            }
        }
    }

    const firstPoi = matchingPois[0];
    return {
        studioFloorId: firstPoi.studioFloorId,
        studioPoiId: firstPoi.studioPoiId,
        buildingId: firstPoi.studioBuildingId,
    };
};

export const getFacilityPoiContentsFromMenu = (menu: BuildingMenu | undefined): PoiContent[] => {
    if (!menu) return [];
    return menu.poiContents ?? [];
};
